import { Injectable, Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import { InjectableSuperclass } from '@sama/ng-core';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { distanceBetween } from '../../../3d/utils/point-utils';
import { eventToPoint2D } from '../../../shared/misc-utilities/point-utils';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import {
  ShapeState,
  ShapeState2D,
} from '../../annotation/shared/state/shape-state';
import { BrowserControlService } from '../browser-control.service';
import { eventMatches, Modifier } from './event-helper';

const mouseMoveThreshold = 5;

export type MouseEventType =
  | 'dblclick'
  | 'click'
  | 'mousedown'
  | 'mousemove'
  | 'mouseout'
  | 'mouseover'
  | 'wheel'
  | 'mouseup';

export enum MouseButton {
  Left = 0,
  Middle = 1,
  Right = 2,
}

export interface MouseEventFilters {
  button?: MouseButton;
  modifiers?: Modifier[] | 'any';
}

export type Target =
  | 'container'
  | 'shape'
  | 'subpoint'
  | 'interactiveBorder'
  | 'wholeShape';

type MouseEventInfoType =
  | MouseEventInfo
  | WheelEventInfo
  | ShapeEventInfo
  | SubpointEventInfo
  | WholeShapeEventInfo
  | InteractiveBorderEventInfo;

export interface MouseEventInfo {
  target: Target;
  event: MouseEvent;
  shapeStore?: Store<ShapeState>;
  subpointIndex?: number;
}

export interface WheelEventInfo extends MouseEventInfo {
  target: 'container';
  event: WheelEvent;
}

export interface ShapeEventInfo extends MouseEventInfo {
  target: 'shape';
  shapeStore: Store<ShapeState2D>;
}

export interface WholeShapeEventInfo extends MouseEventInfo {
  target: 'wholeShape';
  shapeStore: Store<ShapeState2D>;
}

export interface SubpointEventInfo extends MouseEventInfo {
  target: 'subpoint';
  shapeStore: Store<ShapeState2D>;
  subpointIndex: number;
}

export interface InteractiveBorderEventInfo extends MouseEventInfo {
  target: 'interactiveBorder';
  shapeStore: Store<ShapeState2D>;
  lineIndex: number;
}

@Injectable()
export class MouseEventService extends InjectableSuperclass {
  private ignoreNextClickEvent = false;
  private mouseDownEvent?: MouseEvent;

  private eventInfo$ = new Subject<MouseEventInfo>();

  constructor(
    private browserControlService: BrowserControlService,
    injector: Injector,
  ) {
    super(injector);
  }

  getEvent$(
    target: 'container',
    type: 'wheel',
    filters?: MouseEventFilters,
  ): Observable<WheelEventInfo>;

  getEvent$(
    target: 'container',
    type: MouseEventType,
    filters?: MouseEventFilters,
  ): Observable<MouseEventInfo>;

  getEvent$(
    target: 'shape',
    type: MouseEventType,
    filters?: MouseEventFilters,
  ): Observable<ShapeEventInfo>;

  getEvent$(
    target: 'subpoint',
    type: MouseEventType,
    filters?: MouseEventFilters,
  ): Observable<SubpointEventInfo>;

  getEvent$(
    target: 'interactiveBorder',
    type: MouseEventType,
    filters?: MouseEventFilters,
  ): Observable<InteractiveBorderEventInfo>;

  getEvent$(
    target: 'wholeShape',
    type: MouseEventType,
    filters?: MouseEventFilters,
  ): Observable<WholeShapeEventInfo>;

  getEvent$(
    target: Target,
    type: MouseEventType,
    { button = MouseButton.Left, modifiers = [] }: MouseEventFilters = {},
  ) {
    return this.eventInfo$.pipe(
      filter(
        (info) =>
          target === info.target &&
          eventMatches(
            info.event,
            { type, button },
            modifiers,
            this.browserControlService.getPlatformModifier(),
          ),
      ),
    );
  }

  handle(info: MouseEventInfoType) {
    switch (info.event.type) {
      case 'mousedown':
        this.mouseDownEvent = info.event;
        break;
      case 'mousemove':
        this.ignoreNextClickEventIfUsingLeftButton(info.event);
        break;
      case 'click':
        this.mouseDownEvent = undefined;
        if (this.ignoreNextClickEvent) {
          this.ignoreNextClickEvent = false;
          return;
        }
        break;
    }

    this.eventInfo$.next(info);
  }

  // If the user mouses down then up within the same DOM node using the left
  // mouse button, the browser will issue a `click` event in addition to
  // `mouseup` and `mousedown`. We ignore the ensuing `click` event to prevent
  // unintended changes to the scene after the user finishes dragging.
  private ignoreNextClickEventIfUsingLeftButton(event: MouseEvent) {
    if (
      this.mouseDownEvent?.button === MouseButton.Left &&
      !isWithinTolerance(
        eventToPoint2D(this.mouseDownEvent),
        eventToPoint2D(event),
      )
    ) {
      this.ignoreNextClickEvent = true;
    }
  }
}

export function isWithinTolerance(startPoint: Point2D, endPoint: Point2D) {
  return distanceBetween(startPoint, endPoint) <= mouseMoveThreshold;
}
